import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ApiClient } from '../api/client';
import type { PageItem, FieldItem, RecordItem, Tenant } from '../types';

export async function downloadPageXLSX(pageId: number, pageTitle: string, slug?: string): Promise<void> {
  try {
    const blob = await ApiClient.exportPageXLSX(pageId);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const safeSlug = (slug || pageTitle)
      .toLowerCase()
      .replace(/[áéíóöőúüű]/g, (m) => ({ á: 'a', é: 'e', í: 'i', ó: 'o', ö: 'o', ő: 'o', ú: 'u', ü: 'u', ű: 'u' }[m] || m))
      .replace(/[^a-z0-9]+/g, '_')
      .slice(0, 35);

    a.download = `${safeSlug}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err: any) {
    console.error('XLSX export hiba:', err);
    throw err;
  }
}

export async function downloadPagePDF(
  page: PageItem,
  fields: FieldItem[],
  records: RecordItem[],
  tenant: Tenant | null
): Promise<void> {
  try {
    // Normalization helper for PDF standard fonts (handles Hungarian accented characters)
    const pdfNorm = (s: any) =>
      String(s ?? '')
        .replace(/ő/g, 'ö')
        .replace(/Ő/g, 'Ö')
        .replace(/ű/g, 'ü')
        .replace(/Ű/g, 'Ü');

    // Fetch latest branding if available
    let brand: Partial<Tenant> = tenant || {};
    try {
      const res = await ApiClient.getBranding();
      if (res.ok && res.data) {
        brand = { ...brand, ...res.data };
      }
    } catch {
      // fallback to tenant
    }

    const isLand = fields.length > 4;
    const doc = new jsPDF({ orientation: isLand ? 'landscape' : 'portrait' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();

    const hexRGB = (hex?: string): [number, number, number] => {
      const h = (hex || '#1a5fd4').replace('#', '');
      return [
        parseInt(h.slice(0, 2), 16) || 26,
        parseInt(h.slice(2, 4), 16) || 95,
        parseInt(h.slice(4, 6), 16) || 212,
      ];
    };
    const [cr, cg, cb] = hexRGB(brand.brand_color);

    const layout = brand.header_layout || 'contact_left';
    const tenantName = brand.name || tenant?.name || 'KözzéTér Pro.';
    const nameText = pdfNorm(tenantName);
    const leftLines = (brand.header_left || (tenant?.address ? `CÍM: ${tenant.address}` : ''))
      .split('\n')
      .map(pdfNorm)
      .filter(Boolean);

    const startY = 12;
    const lineH = 4.5;
    const addrH = Math.max(leftLines.length * lineH, 10);

    // Draw header contacts & name
    if (layout === 'name_left') {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.setTextColor(cr, cg, cb);
      doc.text(nameText, 14, 18);

      doc.setFontSize(7.5);
      leftLines.forEach((line, i) => {
        const ci = line.indexOf(':');
        if (ci > 0) {
          const key = line.slice(0, ci + 1);
          const val = ' ' + line.slice(ci + 1).trim();
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(cr, cg, cb);
          doc.text(key, pageW - 14 - doc.getTextWidth(val), startY + i * lineH, { align: 'right' });
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(60);
          doc.text(val, pageW - 14, startY + i * lineH, { align: 'right' });
        } else {
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(60);
          doc.text(line, pageW - 14, startY + i * lineH, { align: 'right' });
        }
      });
    } else {
      // Default: contact_left, name_right
      doc.setFontSize(7.5);
      leftLines.forEach((line, i) => {
        const ci = line.indexOf(':');
        if (ci > 0) {
          const key = line.slice(0, ci + 1);
          const val = ' ' + line.slice(ci + 1).trim();
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(cr, cg, cb);
          doc.text(key, 14, startY + i * lineH);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(60);
          doc.text(val, 14 + doc.getTextWidth(key), startY + i * lineH);
        } else {
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(60);
          doc.text(line, 14, startY + i * lineH);
        }
      });

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.setTextColor(cr, cg, cb);
      doc.text(nameText, pageW - 14, 18, { align: 'right' });
    }

    // Color divider bar
    const vy = Math.max(28, startY + addrH + 2);
    doc.setDrawColor(cr, cg, cb);
    doc.setLineWidth(0.6);
    doc.line(14, vy, pageW - 14, vy);

    // Page title
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30);
    doc.text(pdfNorm(page.title), 14, vy + 6);

    // Filter fields & fallback if empty
    let displayFields = fields.filter((f) => f.name !== 'pont');
    if (displayFields.length === 0) {
      displayFields = [
        { id: -1, page_id: page.id, name: 'megnevezes', label: 'Megnevezés', type: 'text', sort_order: 1, required: 0 },
        { id: -2, page_id: page.id, name: 'ertek', label: 'Tartalom / Közzétett adat', type: 'textarea', sort_order: 2, required: 0 },
      ];
    }

    // Prepare table headers and body
    const tableHeaders = [
      '#',
      'Pont',
      ...displayFields.map((f) => pdfNorm(f.label)),
      'Melléklet',
      'Állapot'
    ];

    const tableBody = records.map((r, i) => {
      let d: any = {};
      try {
        d = typeof r.data === 'string' ? JSON.parse(r.data) : (r.data || {});
      } catch {
        d = {};
      }
      if ((r as any).parsedData && typeof (r as any).parsedData === 'object') {
        d = { ...d, ...(r as any).parsedData };
      }
      if (d && typeof d === 'object' && d.data && typeof d.data === 'object' && !Array.isArray(d.data)) {
        d = { ...d.data, ...d };
      }

      const isComplete = !fields.some((f) => f.required && !d[f.name]);
      const statusText = isComplete ? 'Rendben' : 'Hiányos';
      const attText = r.attachment_count && r.attachment_count > 0 ? `${r.attachment_count} db` : '–';

      const fieldValues = displayFields.map((f) => {
        let val = d[f.name];
        if (val === undefined || val === null || val === '') {
          if (f.name === 'megnevezes') val = d.cim || d.nev || d.title || '–';
          else if (f.name === 'cim') val = d.megnevezes || d.nev || d.title || '–';
          else if (f.name === 'ertek') val = d.tartalom || d.leiras || '–';
          else if (f.name === 'leiras') val = d.ertek || d.tartalom || '–';
          else val = '–';
        }
        if (typeof val === 'object') val = '–';
        return pdfNorm(String(val ?? '–').slice(0, 160));
      });

      return [
        String(i + 1),
        pdfNorm(d.pont || `${i + 1}. pont`),
        ...fieldValues,
        pdfNorm(attText),
        pdfNorm(statusText),
      ];
    });

    const totalPagesExp = '{total_pages_count_string}';

    // AutoTable
    autoTable(doc, {
      head: [tableHeaders],
      body: tableBody,
      startY: vy + 10,
      margin: { left: 14, right: 14, bottom: 16 },
      styles: {
        font: 'helvetica',
        fontSize: 7.5,
        cellPadding: 2.2,
        textColor: 40,
        overflow: 'linebreak',
      },
      headStyles: {
        fillColor: [cr, cg, cb],
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 8,
      },
      alternateRowStyles: {
        fillColor: [247, 249, 252],
      },
      didDrawPage: (data) => {
        // Footer on every page
        const y = pageH - 7;
        const lf = pdfNorm(brand.footer_left || 'Bizalmas');
        const cf = pdfNorm(brand.footer_center || tenantName);
        let rf = brand.footer_right || '{page} / {pages} oldal';
        rf = pdfNorm(rf.replace('{page}', String(data.pageNumber)).replace('{pages}', totalPagesExp));

        doc.setFontSize(6.5);
        doc.setTextColor(140);
        doc.setFont('helvetica', 'normal');
        doc.setDrawColor(210);
        doc.setLineWidth(0.25);
        doc.line(14, y - 3, pageW - 14, y - 3);

        if (lf) doc.text(lf, 14, y, { align: 'left' });
        if (cf) doc.text(cf, pageW / 2, y, { align: 'center' });
        if (rf) doc.text(rf, pageW - 14, y, { align: 'right' });
      },
    });

    if (typeof (doc as any).putTotalPages === 'function') {
      (doc as any).putTotalPages(totalPagesExp);
    }

    const safeSlug = (page.slug || page.title)
      .toLowerCase()
      .replace(/[áéíóöőúüű]/g, (m) => ({ á: 'a', é: 'e', í: 'i', ó: 'o', ö: 'o', ő: 'o', ú: 'u', ü: 'u', ű: 'u' }[m] || m))
      .replace(/[^a-z0-9]+/g, '_')
      .slice(0, 35);

    doc.save(`${safeSlug}_${new Date().toISOString().slice(0, 10)}.pdf`);
  } catch (err: any) {
    console.error('PDF export hiba:', err);
    throw err;
  }
}

export async function downloadTenantsXLSX(): Promise<void> {
  try {
    const blob = await ApiClient.exportTenantsXLSX();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `szervezetek_${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err) {
    console.error('Szervezetek XLSX hiba:', err);
    throw err;
  }
}

export async function downloadTenantsPDF(tenants: Tenant[]): Promise<void> {
  try {
    const pdfNorm = (s: any) =>
      String(s ?? '')
        .replace(/ő/g, 'ö')
        .replace(/Ő/g, 'Ö')
        .replace(/ű/g, 'ü')
        .replace(/Ű/g, 'Ü');

    const doc = new jsPDF({ orientation: 'landscape' });

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(30, 41, 59);
    doc.text(pdfNorm('KözzéTér Pro. – Szervezetek és Intézmények'), 14, 18);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(pdfNorm(`Generálva: ${new Date().toLocaleString('hu-HU')} | Összesen: ${tenants.length} szervezet`), 14, 25);

    const tableHeaders = ['ID', 'Szervezet Neve', 'Azonosító (Slug)', 'Rövid Név', 'E-mail', 'Telefon', 'Székhely', 'Státusz'];
    const tableBody = tenants.map((t) => [
      String(t.id),
      pdfNorm(t.name),
      pdfNorm(t.slug),
      pdfNorm(t.short_name || '–'),
      pdfNorm(t.email || '–'),
      pdfNorm(t.phone || '–'),
      pdfNorm(t.address || '–'),
      pdfNorm(t.active ? 'Aktív' : 'Inaktív'),
    ]);

    autoTable(doc, {
      head: [tableHeaders],
      body: tableBody,
      startY: 30,
      margin: { left: 14, right: 14, bottom: 14 },
      styles: { font: 'helvetica', fontSize: 8, cellPadding: 2.5 },
      headStyles: { fillColor: [79, 70, 229], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    });

    doc.save(`szervezetek_${new Date().toISOString().slice(0, 10)}.pdf`);
  } catch (err) {
    console.error('Szervezetek PDF hiba:', err);
    throw err;
  }
}

export async function downloadComplianceXLSX(): Promise<void> {
  try {
    const blob = await ApiClient.exportComplianceXLSX();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `megfelelosegi_riport_${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err) {
    console.error('Megfelelőségi XLSX hiba:', err);
    throw err;
  }
}

export async function downloadCompliancePDF(overviewData: any[]): Promise<void> {
  try {
    const pdfNorm = (s: any) =>
      String(s ?? '')
        .replace(/ő/g, 'ö')
        .replace(/Ő/g, 'Ö')
        .replace(/ű/g, 'ü')
        .replace(/Ű/g, 'Ü');

    const doc = new jsPDF({ orientation: 'landscape' });

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(30, 41, 59);
    doc.text(pdfNorm('KözzéTér Pro. – Megfelelőségi és Közzétételi Riport'), 14, 18);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(pdfNorm(`Generálva: ${new Date().toLocaleString('hu-HU')} | Vizsgált intézmények száma: ${overviewData.length}`), 14, 25);

    const headers = ['Szervezet Neve', 'Azonosító', 'Kitöltöttség %', 'Elvárt tétel', 'Kitöltve', 'Hiányos / hiányzó', 'Státusz'];
    const body = overviewData.map((item) => [
      pdfNorm(item.tenant_name),
      pdfNorm(item.tenant_slug),
      item.total_records === 0 ? '– %' : `${item.percentage}%`,
      String(item.total_expected ?? item.total_records),
      String(item.completed_records),
      item.total_records === 0 ? '–' : item.missing_required_count > 0 ? `${item.missing_required_count} db hiány` : 'Nincs hiány',
      item.total_records === 0 
        ? 'Feltöltésre vár' 
        : item.missing_required_count > 0 
          ? 'Hiányos' 
          : 'Megfelelő',
    ]);

    autoTable(doc, {
      head: [headers],
      body: body,
      startY: 30,
      margin: { left: 14, right: 14, bottom: 14 },
      styles: { font: 'helvetica', fontSize: 8.5, cellPadding: 2.5 },
      headStyles: { fillColor: [79, 70, 229], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    });

    const allMissing: any[] = [];
    for (const item of overviewData) {
      for (const m of (item.missing_items || [])) {
        allMissing.push({ ...m, tenant_name: item.tenant_name });
      }
      for (const u of (item.uncovered_points || [])) {
        allMissing.push({ tenant_name: item.tenant_name, page_title: u.chapter, pont: u.pont, megnevezes: u.cim, reason: 'A 2026-os közzétételi lista pontjához nincs bejegyzés' });
      }
    }

    if (allMissing.length > 0) {
      doc.addPage();
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.setTextColor(225, 29, 72);
      doc.text(pdfNorm('Hiányos és hiányzó tételek részletes listája'), 14, 18);

      const mHeaders = ['Szervezet', 'Adatlap / Téma', 'Pont', 'Megnevezés', 'Hiba / Hiány Oka'];
      const mBody = allMissing.map((m) => [
        pdfNorm(m.tenant_name),
        pdfNorm(m.page_title),
        pdfNorm(m.pont || '–'),
        pdfNorm(m.megnevezes || '–'),
        pdfNorm(m.reason || 'Kötelező melléklet nincs feltöltve'),
      ]);

      autoTable(doc, {
        head: [mHeaders],
        body: mBody,
        startY: 25,
        margin: { left: 14, right: 14, bottom: 14 },
        styles: { font: 'helvetica', fontSize: 7.5, cellPadding: 2 },
        headStyles: { fillColor: [225, 29, 72], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [255, 241, 242] },
      });
    }

    doc.save(`megfelelosegi_riport_${new Date().toISOString().slice(0, 10)}.pdf`);
  } catch (err) {
    console.error('Megfelelőségi PDF hiba:', err);
    throw err;
  }
}

export async function downloadAuditLogXLSX(params?: { action?: string; tenant_id?: number; search?: string }): Promise<void> {
  try {
    const blob = await ApiClient.exportAuditXLSX(params);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `muveleti_naplo_audit_${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err) {
    console.error('Audit XLSX hiba:', err);
    throw err;
  }
}

export async function downloadAuditLogPDF(logs: any[], filterTitle?: string): Promise<void> {
  try {
    const pdfNorm = (s: any) =>
      String(s ?? '')
        .replace(/ő/g, 'ö')
        .replace(/Ő/g, 'Ö')
        .replace(/ű/g, 'ü')
        .replace(/Ű/g, 'Ü');

    const doc = new jsPDF({ orientation: 'landscape' });

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(30, 41, 59);
    doc.text(pdfNorm('KözzéTér Pro. – Műveleti Napló (Audit Trail)'), 14, 18);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100);
    const subtitle = `Exportálva: ${new Date().toLocaleString('hu-HU')} | Tételek száma: ${logs.length}${filterTitle ? ' | Szűrés: ' + filterTitle : ''}`;
    doc.text(pdfNorm(subtitle), 14, 25);

    const headers = ['Időpont', 'Művelet', 'Entitás', 'Felhasználó', 'Intézmény', 'Részletek'];

    const getHuAction = (act: string) => {
      if (!act) return 'Egyéb';
      const a = act.toLowerCase();
      if (a === 'login') return 'Bejelentkezés';
      if (a === 'logout') return 'Kijelentkezés';
      if (a === 'impersonate') return 'Megszemélyesítés';
      if (a.includes('record_update') || a.includes('record_edit')) return 'Rekord módosítása';
      if (a.includes('record_create') || a.includes('record_add')) return 'Új rekord';
      if (a.includes('record_delete')) return 'Rekord törlése';
      if (a.includes('attachment_download')) return 'Fájl letöltés';
      if (a.includes('attachment_preview')) return 'Fájl előnézet';
      if (a.includes('attachment_upload')) return 'Fájl feltöltés';
      if (a.includes('attachment_delete')) return 'Fájl törlés';
      if (a.includes('attachment_rename')) return 'Fájl átnevezés';
      if (a.includes('attachment_move')) return 'Fájl áthelyezés';
      if (a.includes('attachment_copy')) return 'Fájl másolás';
      if (a.includes('attachment_export_zip')) return 'Fájlok ZIP export';
      if (a.includes('sync_push')) return 'Szinkron Push';
      if (a.includes('sync_pull')) return 'Szinkron Pull';
      if (a.includes('scrape') || a.includes('staging')) return 'Web átemelés';
      if (a.includes('page')) return 'Oldalstruktúra';
      if (a.includes('field')) return 'Meződefiníció';
      if (a.includes('user')) return 'Felhasználókezelés';
      if (a.includes('export')) return 'Adat exportálás';
      return act;
    };

    const body = logs.map((l) => {
      let detailStr = '';
      try {
        const d = typeof l.detail === 'string' ? JSON.parse(l.detail) : l.detail;
        if (d) {
          if (d.filename) detailStr += `Fájl: ${d.filename} `;
          if (d.page_title) detailStr += `Oldal: ${d.page_title} `;
          if (d.appliedCount !== undefined) detailStr += `Alkalmazva: ${d.appliedCount} db `;
          if (d.conflictCount !== undefined) detailStr += `Ütközés: ${d.conflictCount} db `;
          if (!detailStr) detailStr = JSON.stringify(d);
        }
      } catch {
        detailStr = String(l.detail || '');
      }

      return [
        l.created_at ? new Date(l.created_at).toLocaleString('hu-HU') : '–',
        pdfNorm(getHuAction(l.action)),
        pdfNorm(`${l.entity || ''} ${l.entity_id ? '#' + l.entity_id : ''}`.trim()),
        pdfNorm(l.user_name || l.user_email || (l.user_id ? `User #${l.user_id}` : 'Rendszer')),
        pdfNorm(l.tenant_name || '–'),
        pdfNorm(detailStr.slice(0, 100)),
      ];
    });

    autoTable(doc, {
      head: [headers],
      body: body,
      startY: 30,
      margin: { left: 14, right: 14, bottom: 14 },
      styles: { font: 'helvetica', fontSize: 7.5, cellPadding: 2 },
      headStyles: { fillColor: [79, 70, 229], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    });

    doc.save(`audit_naplo_${new Date().toISOString().slice(0, 10)}.pdf`);
  } catch (err) {
    console.error('Audit PDF hiba:', err);
    throw err;
  }
}
